import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, FolderOpen, Search, Trash2, Download, FileText, Upload, FolderUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
    alerte_mobilisation: 'border-l-red-500',
    evacuation: 'border-l-orange-500',
    hebergement_care: 'border-l-blue-500',
    communication_crise: 'border-l-green-500',
    ravitaillement: 'border-l-purple-500',
    retour_normale: 'border-l-teal-500',
    autre: 'border-l-zinc-500',
};

const ProceduresPage = () => {
    const { token, isChefDispositif } = useAuth();
    const [procedures, setProcedures] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filterCat, setFilterCat] = useState('all');
    const [search, setSearch] = useState('');
    const [formData, setFormData] = useState({ title: '', category: 'autre', description: '' });
    const [file, setFile] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFiles, setBulkFiles] = useState([]);
    const [bulkCat, setBulkCat] = useState('autre');
    const [bulkBusy, setBulkBusy] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [procRes, catRes] = await Promise.all([
                axios.get(`${API}/procedures`, { headers }),
                axios.get(`${API}/procedures/categories`, { headers })
            ]);
            setProcedures(procRes.data);
            setCategories(catRes.data);
        } catch { toast.error('Erreur chargement'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) { toast.error('Veuillez joindre un fichier'); return; }
        const fd = new FormData();
        fd.append('title', formData.title);
        fd.append('category', formData.category);
        fd.append('description', formData.description);
        fd.append('content', '');
        fd.append('file', file);
        try {
            await axios.post(`${API}/procedures`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
            toast.success('Procedure ajoutee');
            setDialogOpen(false);
            setFormData({ title: '', category: 'autre', description: '' });
            setFile(null);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette procedure ?')) return;
        try {
            await axios.delete(`${API}/procedures/${id}`, { headers });
            toast.success('Supprimee');
            fetchData();
        } catch { toast.error('Erreur'); }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        if (bulkFiles.length === 0) { toast.error('Sélectionnez au moins un fichier'); return; }
        setBulkBusy(true);
        const fd = new FormData();
        bulkFiles.forEach(f => fd.append('files', f));
        fd.append('category', bulkCat);
        try {
            const res = await axios.post(`${API}/procedures/bulk`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
            toast.success(`${res.data.created} document(s) importé(s)`);
            if (res.data.errors?.length) {
                res.data.errors.forEach(er => toast.error(`${er.file} : ${er.detail}`));
            }
            setBulkOpen(false);
            setBulkFiles([]);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur import');
        } finally {
            setBulkBusy(false);
        }
    };

    const handleRecategorize = async (procId, category) => {
        try {
            await axios.put(`${API}/procedures/${procId}/category`, { category }, { headers });
            toast.success(`Reclassé dans "${catLabel(category)}"`);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const handleDownload = (proc) => {
        // Direct link - handled by <a> tags in JSX
    };

    const catLabel = (id) => categories.find(c => c.id === id)?.label || id;

    const filtered = procedures.filter(p => {
        if (filterCat !== 'all' && p.category !== filterCat) return false;
        if (search && !p.title.toLowerCase().includes(search.toLowerCase()) && !(p.description || '').toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const grouped = {};
    filtered.forEach(p => {
        const cat = p.category || 'autre';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(p);
    });

    if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;

    return (
        <div className="space-y-6" data-testid="procedures-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="text-3xl font-bold text-white">Procedures communales</h1>
                    <p className="page-subtitle">Documents de reference pour la gestion de crise</p>
                </div>
                {isChefDispositif && (
                    <div className="flex gap-2">
                        <Button variant="outline" className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10" onClick={() => { setBulkFiles([]); setBulkCat('autre'); setBulkOpen(true); }} data-testid="bulk-upload-btn">
                            <FolderUp className="w-4 h-4 mr-2" /> Importer un dossier
                        </Button>
                        <Button className="tactical-btn" onClick={() => { setFormData({ title: '', category: 'autre', description: '' }); setFile(null); setDialogOpen(true); }} data-testid="add-procedure-btn">
                            <Plus className="w-4 h-4 mr-2" /> Ajouter une procedure
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-xs">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} className="tactical-input pl-9" placeholder="Rechercher..." data-testid="search-procedures" />
                </div>
                <Select value={filterCat} onValueChange={setFilterCat}>
                    <SelectTrigger className="tactical-input w-[200px]" data-testid="filter-category">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">Toutes les categories</SelectItem>
                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Badge variant="outline" className="text-zinc-400 border-zinc-700">{filtered.length} procedure(s)</Badge>
            </div>

            {Object.entries(grouped).map(([cat, procs]) => (
                <div key={cat} className="space-y-2">
                    <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                        <FolderOpen className="w-4 h-4" /> {catLabel(cat)} ({procs.length})
                    </h2>
                    <div className="grid gap-2">
                        {procs.map(proc => (
                            <Card key={proc.id} className={`content-card border-l-4 ${CATEGORY_COLORS[proc.category] || CATEGORY_COLORS.autre}`} data-testid={`procedure-${proc.id}`}>
                                <CardContent className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <FileText className="w-5 h-5 text-blue-400 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-white font-medium truncate">{proc.title}</p>
                                            {proc.description && <p className="text-xs text-zinc-500 truncate">{proc.description}</p>}
                                            <p className="text-[10px] text-zinc-600 mt-0.5">{proc.file_name || 'Pas de fichier'} - par {proc.created_by}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0 ml-3">
                                        {isChefDispositif && (
                                            <Select value={proc.category || 'autre'} onValueChange={(v) => handleRecategorize(proc.id, v)}>
                                                <SelectTrigger className="tactical-input h-8 w-[170px] text-xs" data-testid={`recat-${proc.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        )}
                                        {proc.file_name && (
                                            <a href={`${API}/procedures/${proc.id}/file?token=${token}`}
                                                target="_blank" rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-medium bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors"
                                                data-testid={`download-${proc.id}`}>
                                                <Download className="w-3.5 h-3.5" /> Telecharger
                                            </a>
                                        )}
                                        {isChefDispositif && (
                                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => handleDelete(proc.id)} data-testid={`delete-${proc.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            ))}

            {filtered.length === 0 && (
                <Card className="content-card"><CardContent className="p-8 text-center text-zinc-500"><FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />Aucune procedure</CardContent></Card>
            )}

            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800" data-testid="bulk-upload-modal">
                    <DialogHeader><DialogTitle>Importer un dossier de documents</DialogTitle></DialogHeader>
                    <form onSubmit={handleBulkSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Categorie appliquée à tous les documents</Label>
                            <Select value={bulkCat} onValueChange={setBulkCat}>
                                <SelectTrigger className="tactical-input" data-testid="bulk-category"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <p className="text-[10px] text-zinc-500 mt-1">Vous pourrez reclassifier chaque document individuellement après l'import.</p>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Documents *</Label>
                            <div className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center hover:border-blue-500/50 transition-colors cursor-pointer" onClick={() => document.getElementById('bulk-files-input').click()}>
                                <FolderUp className="w-6 h-6 mx-auto mb-2 text-zinc-500" />
                                {bulkFiles.length > 0 ? (
                                    <div className="text-left max-h-36 overflow-y-auto space-y-0.5" data-testid="bulk-files-list">
                                        {bulkFiles.map((f, i) => <p key={`${f.name}-${i}`} className="text-xs text-blue-400 truncate">• {f.name}</p>)}
                                    </div>
                                ) : (
                                    <p className="text-sm text-zinc-500">Cliquez pour sélectionner plusieurs fichiers<br /><span className="text-[10px]">PDF, DOC, DOCX, ODT, JPG, PNG — le titre = nom du fichier</span></p>
                                )}
                            </div>
                            <input id="bulk-files-input" type="file" multiple accept=".pdf,.doc,.docx,.odt,.jpg,.png" onChange={(e) => setBulkFiles(Array.from(e.target.files))} className="hidden" data-testid="bulk-files-input" />
                            {bulkFiles.length > 0 && <p className="text-xs text-zinc-400 mt-1">{bulkFiles.length} fichier(s) sélectionné(s)</p>}
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button type="button" variant="ghost" onClick={() => setBulkOpen(false)}>Annuler</Button>
                            <Button type="submit" className="tactical-btn" disabled={bulkFiles.length === 0 || bulkBusy} data-testid="bulk-submit">
                                {bulkBusy ? 'Import en cours...' : `Importer ${bulkFiles.length || ''} document(s)`}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                    <DialogHeader><DialogTitle>Ajouter une procedure</DialogTitle></DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Titre *</Label>
                            <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="tactical-input" required data-testid="proc-title" placeholder="Ex: Plan d'evacuation zone sud" />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Categorie</Label>
                            <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                                <SelectTrigger className="tactical-input" data-testid="proc-category"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Description courte</Label>
                            <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="tactical-input" placeholder="Optionnel" data-testid="proc-description" />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Document *</Label>
                            <div className="border-2 border-dashed border-zinc-700 rounded-lg p-4 text-center hover:border-blue-500/50 transition-colors cursor-pointer" onClick={() => document.getElementById('proc-file-input').click()}>
                                <Upload className="w-6 h-6 mx-auto mb-2 text-zinc-500" />
                                {file ? (
                                    <p className="text-sm text-blue-400">{file.name}</p>
                                ) : (
                                    <p className="text-sm text-zinc-500">Cliquez pour selectionner un fichier<br /><span className="text-[10px]">PDF, DOC, DOCX, ODT, JPG, PNG</span></p>
                                )}
                            </div>
                            <input id="proc-file-input" type="file" accept=".pdf,.doc,.docx,.odt,.jpg,.png" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="proc-file" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                            <Button type="submit" className="tactical-btn" disabled={!file} data-testid="proc-submit">Ajouter</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProceduresPage;
